import type { FC, ReactNode } from "react";
import { Dialog, Heading } from "react-aria-components";
import { Button, ButtonVariant } from "../Buttons/button.js";

export interface AlertDialogProps {
  variant?: AlertDialogVariant;
  title: ReactNode;
  children?: ReactNode;
  /** Optional content shown at the start of the footer, before the actions. */
  footer?: ReactNode;
  confirmText: ReactNode;
  cancelText?: ReactNode;
  isConfirming?: boolean;
  onConfirm?: (context: { close: () => void }) => void;
}

export const AlertDialogVariant = {
  Confirm: "CONFIRM",
  Destructive: "DESTRUCTIVE",
} as const;

export type AlertDialogVariant =
  (typeof AlertDialogVariant)[keyof typeof AlertDialogVariant];

const confirmButtonVariants: Record<AlertDialogVariant, ButtonVariant> = {
  [AlertDialogVariant.Confirm]: ButtonVariant.Primary,
  [AlertDialogVariant.Destructive]: ButtonVariant.Destructive,
};

export const AlertDialog: FC<AlertDialogProps> = (props) => {
  const { variant = AlertDialogVariant.Confirm, onConfirm } = props;

  return (
    <Dialog role="alertdialog" className="space-y-4 p-4 sm:p-6">
      {({ close }) => (
        <>
          <Heading
            slot="title"
            className="text-base font-semibold text-foreground"
          >
            {props.title}
          </Heading>

          {props.children ? (
            <div className="text-sm text-muted-foreground">
              {props.children}
            </div>
          ) : null}

          <div className="flex flex-wrap items-center gap-4">
            {props.footer ? <div>{props.footer}</div> : null}

            <div className="ml-auto flex shrink-0 items-center gap-2">
              <Button slot="close" variant={ButtonVariant.Secondary}>
                {props.cancelText ?? "Cancel"}
              </Button>

              <Button
                variant={
                  confirmButtonVariants[variant] ?? ButtonVariant.Primary
                }
                isLoading={props.isConfirming}
                isDisabled={!onConfirm}
                onClick={onConfirm ? () => onConfirm({ close }) : undefined}
              >
                {props.confirmText}
              </Button>
            </div>
          </div>
        </>
      )}
    </Dialog>
  );
};
